import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone, Twitter, Youtube } from "lucide-react";
import { useRegionalExperience } from "../../hooks/useRegionalExperience";
import { useTranslation } from "react-i18next";
import { openCookieSettings } from "../../lib/cookieConsent";

const Footer = () => {
  const { region } = useRegionalExperience();
  const { t } = useTranslation();

  const programs = [
    [t("footer.programs.healthcare"), "/programs/healthcare"],
    [t("footer.programs.futureSkilling"), "/programs/future-skilling"],
    [t("footer.programs.schoolEducation"), "/programs/school"],
    [t("footer.programs.womensDignity"), "/programs/mass-wedding"],
    [t("footer.programs.mahakumbhSeva"), "/programs/mahakumbh"],
    [t("footer.programs.vedanticWisdom"), "/programs/vedant"],
  ];

  return (
    <footer className="border-t border-[#ead9cd] bg-[#2a120d] text-white">
      <div className="container mx-auto px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr]">
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src="/assets/Avdheshanandg mission logo.png"
                alt={t("footer.logoAlt")}
                className="h-16 w-auto rounded bg-white p-1"
              />
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em]">
                  {t("footer.orgName")}
                </p>
                <p className="mt-1 text-sm text-white/65">{t("footer.tagline")}</p>
              </div>
            </Link>
            <p className="mt-6 max-w-md text-sm leading-7 text-white/70">
              {t("footer.description")}
            </p>
            <div className="mt-6 flex gap-2">
              {[
                [Twitter, "https://twitter.com/AvdheshanandG", t("footer.social.twitter")],
                [Facebook, "https://www.facebook.com/AvdheshanandG/", t("footer.social.facebook")],
                [
                  Instagram,
                  "https://www.instagram.com/avdheshanandg_official/",
                  t("footer.social.instagram"),
                ],
                [Youtube, "https://www.youtube.com/@avdheshanandg", t("footer.social.youtube")],
              ].map(([Icon, href, label]) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-white/75 transition hover:bg-white hover:text-[#4b2418]"
                  aria-label={label}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white/90">
              {t("footer.sections.programs")}
            </h2>
            <ul className="mt-5 space-y-3 text-sm text-white/68">
              {programs.map(([program, path]) => (
                <li key={program}>
                  <Link to={path} className="transition hover:text-white">
                    {program}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white/90">
              {t("footer.sections.website")}
            </h2>
            <div className="mt-5 grid gap-3 text-sm">
              <Link to="/about" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.aboutAgm")}
              </Link>
              <Link to="/impact" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.impact")}
              </Link>
              <Link to="/programs" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.programs")}
              </Link>
              <Link to="/vedant" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.vedant")}
              </Link>
              <Link to="/global-presence" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.globalPresence")}
              </Link>
              <Link to="/reports" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.reports")}
              </Link>
              <Link to="/upcoming-event" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.upcomingEvent")}
              </Link>
              <Link to="/booking/find" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.manageRegistration")}
              </Link>
              <Link to="/blogs" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.blogs")}
              </Link>
              <Link to="/articles" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.articles")}
              </Link>
              <Link to="/volunteer" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.volunteer")}
              </Link>
              <Link to="/privacy-policy" className="text-white/68 transition hover:text-white">
                {t("footer.links.privacyPolicy")}
              </Link>
              <Link to="/refund-policy" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.refundPolicy")}
              </Link>
              <Link to="/terms" className="text-white/68 transition hover:text-white">
                {t("footer.links.terms")}
              </Link>
              <Link to="/transparency" className="text-white/68 transition hover:text-white">
                {t("footer.websiteLinks.transparency")}
              </Link>
              <Link to="/cookie-policy" className="text-white/68 transition hover:text-white">
                Cookie Policy
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                className="text-left text-white/68 transition hover:text-white"
              >
                Cookie settings
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white/90">
              {t("footer.sections.contact")}
            </h2>
            <div className="mt-5 space-y-4 text-sm text-white/68">
              <p className="flex gap-3 leading-6">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#f5c45e]" />
                <span className="whitespace-pre-line">{t("footer.contactAddress")}</span>
              </p>
              <a href="tel:+17472549029" className="flex gap-3 transition hover:text-white">
                <Phone className="h-4 w-4 shrink-0 text-[#f5c45e]" />
                +1 (747) 254-9029
              </a>
              <a
                href="mailto:info@avdheshanandgmission.org"
                className="flex gap-3 transition hover:text-white"
              >
                <Mail className="h-4 w-4 shrink-0 text-[#f5c45e]" />
                info@avdheshanandgmission.org
              </a>
            </div>
            <Link
              to={region.donationPath}
              className="mt-6 inline-flex rounded-md bg-[#f5c45e] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#201711] transition hover:bg-[#ffd77c]"
            >
              {region.paymentLabel}
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/12 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
          <p>{t("footer.copyrightNew", { year: new Date().getFullYear() })}</p>
          <p>{t("footer.sevaTagline")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
